import React from "react";
import { useInterviewContext } from "../contexts/InterviewContext";

const QuestionDisplay = () => {
  const { questions, currentQuestionIndex } = useInterviewContext();

  if (
    !questions ||
    questions.length === 0 ||
    currentQuestionIndex >= questions.length
  ) {
    return (
      // Removed outer specific background/border/shadow, assumes parent provides card-like container.
      // Text colors will be handled by specific elements or inherited.
      <div className="my-4">
        <svg
          className="h-12 w-12 text-yellow-400 mx-auto mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <h3 className="text-lg font-medium text-yellow-300 mb-2">
          No Questions Available
        </h3>
        <p className="text-yellow-400">
          The interview questions haven't been loaded or the interview has
          finished.
        </p>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  // Dark Theme Color Sets
  const questionTypeColors = {
    behavioral: {
      bgHeader: "bg-blue-900/30", // Slightly transparent dark blue
      border: "border-blue-700/50",
      textHeader: "text-blue-300",
      badgeBg: "bg-blue-500/30",
      badgeText: "text-blue-200",
      icon: (
        <svg
          className="h-5 w-5 text-blue-400" // Adjusted icon color for dark theme
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
          />
        </svg>
      ),
    },
    technical: {
      bgHeader: "bg-purple-900/30",
      border: "border-purple-700/50",
      textHeader: "text-purple-300",
      badgeBg: "bg-purple-500/30",
      badgeText: "text-purple-200",
      icon: (
        <svg
          className="h-5 w-5 text-purple-400" // Adjusted icon color
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
          />
        </svg>
      ),
    },
    situational: {
      bgHeader: "bg-green-900/30",
      border: "border-green-700/50",
      textHeader: "text-green-300",
      badgeBg: "bg-green-500/30",
      badgeText: "text-green-200",
      icon: (
        <svg
          className="h-5 w-5 text-green-400" // Adjusted icon color
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
          />
        </svg>
      ),
    },
    default: {
      bgHeader: "bg-slate-700/40",
      border: "border-slate-600",
      textHeader: "text-slate-300",
      badgeBg: "bg-slate-600/50",
      badgeText: "text-slate-200",
      icon: (
        <svg
          className="h-5 w-5 text-slate-400" // Adjusted icon color
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  };

  // Determine the colors based on question type (case insensitive)
  const questionType = currentQuestion.type
    ? currentQuestion.type.toLowerCase()
    : "default";
  const colorSet =
    questionTypeColors[questionType] || questionTypeColors.default;

  return (
    // The main div class is simplified as the parent component (InterviewPage) now provides the card styling.
    // Added specific padding and structure for the content within the parent's card.
    <div>
      {/* Header Section: Question Type and Progress */}
      <div className={`p-4 sm:p-5 rounded-t-xl ${colorSet.bgHeader} border-b ${colorSet.border}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h2 className={`text-xl sm:text-2xl font-bold ${colorSet.textHeader}`}>
            Question {currentQuestionIndex + 1} of {questions.length}
          </h2>

          <div className="flex items-center mt-2 sm:mt-0">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${colorSet.badgeBg} ${colorSet.badgeText}`}
            >
              {colorSet.icon}
              <span className="ml-1.5">{currentQuestion.type || "General"}</span>
            </span>
            {/* Small progress bar for question type - can be removed if too cluttered */}
            {/* <div className="ml-3 flex items-center">
              <div className="w-full bg-slate-600 rounded-full h-1.5 w-20 sm:w-24">
                <div
                  className="bg-gradient-to-r from-blue-400 to-purple-500 h-1.5 rounded-full"
                  style={{
                    width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
                  }}
                ></div>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      {/* Question Text */}
      <div className={`text-lg font-medium p-5 sm:p-6 text-slate-100 ${colorSet.bgHeader} bg-opacity-50 rounded-b-xl`}>
        {currentQuestion.question}
      </div>

      {/* Helper Text */}
      <div className="mt-5 text-sm text-slate-400 flex items-center">
        <svg
          className="h-4 w-4 mr-1.5 text-slate-500 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <span>Answer the question thoroughly and clearly for best results.</span>
      </div>
    </div>
  );
};

export default QuestionDisplay;
